import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import MobileAvatar from './MobileAvatar';
import AvatarEditor from './AvatarEditor';

export default function MobileUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [cycleAction, setCycleAction] = useState('');
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/mobile_users/${id}`);
      setData(res.data);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load user' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const resetCycle = async (action, confirmText, successText) => {
    if (!window.confirm(confirmText)) return;
    setCycleAction(action);
    try {
      await api.post(`/mobile_users/${id}/${action}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Ciclo',
        detail: successText,
      });
      await load();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo reiniciar',
      });
    } finally {
      setCycleAction('');
    }
  };

  const resetAvatar = async () => {
    if (!window.confirm('¿Resetear el avatar de este usuario?')) return;
    setResetting(true);
    try {
      await api.post(`/mobile_users/${id}/reset-avatar`);
      toast.current?.show({
        severity: 'success',
        summary: 'Avatar',
        detail: 'Avatar reseteado',
      });
      await load();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo resetear el avatar',
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="card p-4">
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading user...
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!data?.user) {
    return (
      <Card>
        <div className="card p-4">
          <p>User not found.</p>
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            className="p-button-text p-button-sm"
            onClick={() => navigate('/mobile_users')}
          />
        </div>
      </Card>
    );
  }

  const { user, loyalty, reports } = data;

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center gap-2">
            <Button
              icon="pi pi-arrow-left"
              className="p-button-text p-button-rounded"
              onClick={() => navigate('/mobile_users')}
              aria-label="Back"
            />
            <MobileAvatar
              avatar={user.avatar}
              name={user.displayName}
              photoUrl={user.photoUrl}
              size={48}
            />
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>{user.displayName || user.email}</span>
            </h2>
          </div>
          <div className="col-6 flex justify-content-end align-items-center">
            <Badge
              className="b-4 relative"
              value={user.points ?? 0}
              style={{ backgroundColor: '#2020A0' }}
            />
          </div>
        </div>

        <div className="grid">
          <div className="col-12 md:col-6">
            <div
              className="p-3 border-round h-full"
              style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
            >
              <h3 className="mt-0 mb-3 main-text">Profile</h3>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Community name:</strong> {user.communityName || '—'}</p>
              <p><strong>Hide photo in reports:</strong> {user.settings?.hidePhotoInReports ? 'Yes' : 'No'}</p>
              <p><strong>Hide real name in reports:</strong> {user.settings?.hideRealNameInReports ? 'Yes' : 'No'}</p>
              <p><strong>Provider:</strong> {user.provider}</p>
              <p><strong>Level:</strong> {user.level}</p>
              <p><strong>Points:</strong> {user.points}</p>
              <p><strong>Reports:</strong> {user.totalReports}</p>
              <p><strong>Reactions:</strong> {user.totalReactions}</p>
              <p><strong>Language:</strong> {user.settings?.language}</p>
              <p className="flex align-items-center gap-2">
                <strong>Avatar:</strong>{' '}
                {user.avatar ? 'Personalizado' : 'Sin avatar'}
                <MobileAvatar
                  avatar={user.avatar}
                  name={user.displayName}
                  photoUrl={user.photoUrl}
                  size={56}
                />
              </p>
              <Button
                type="button"
                label="Resetear avatar"
                icon="pi pi-refresh"
                className="p-button-outlined p-button-danger p-button-sm"
                loading={resetting}
                onClick={resetAvatar}
              />
              <AvatarEditor
                userId={user._id || id}
                avatar={user.avatar}
                name={user.displayName}
                photoUrl={user.photoUrl}
                toast={toast.current}
                onSaved={load}
              />
              <p className="mb-0 mt-3">
                <strong>Created:</strong>{' '}
                {user.createdAt && new Date(user.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div
              className="p-3 border-round h-full"
              style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
            >
              <h3 className="mt-0 mb-3 main-text">Loyalty</h3>
              {loyalty ? (
                <>
                  <p><strong>Level:</strong> {loyalty.level} ({loyalty.levelName})</p>
                  <p><strong>Points:</strong> {loyalty.points}</p>
                  <p><strong>Streak days:</strong> {loyalty.streakDays}</p>
                  <p className="mb-0">
                    <strong>Badges:</strong>{' '}
                    {(loyalty.badges || []).map((b) => b.name || b.id).join(', ') || '—'}
                  </p>
                </>
              ) : (
                <p className="mb-0">No loyalty profile</p>
              )}
            </div>
          </div>
          <div className="col-12">
            <div
              className="p-3 border-round"
              style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
            >
              <h3 className="mt-0 mb-3 main-text">Ciclo inicial</h3>
              <p>
                <strong>Términos:</strong>{' '}
                {user.termsAccepted === true ||
                (user.termsAccepted !== false && user.acceptedTermsVersion)
                  ? 'true'
                  : 'false'}
              </p>
              <p>
                <strong>Onboarding:</strong>{' '}
                {user.onboardingDone === false ? 'false' : 'true'}
              </p>
              <p>
                <strong>Setup:</strong>{' '}
                {user.setupDone === false ? 'false' : 'true'}
              </p>
              <p className="text-600 mt-0" style={{ fontSize: '0.9rem' }}>
                En el login se piden solo si el valor está en false. Una cuenta nueva nace en false. Una cuenta vieja sin el campo cuenta como true.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  label="Reiniciar términos"
                  icon="pi pi-file"
                  className="p-button-outlined p-button-sm"
                  loading={cycleAction === 'reset-terms'}
                  disabled={!!cycleAction}
                  onClick={() =>
                    resetCycle(
                      'reset-terms',
                      '¿Pedir de nuevo la aceptación de términos?',
                      'Términos reiniciados',
                    )
                  }
                />
                <Button
                  type="button"
                  label="Reiniciar onboarding"
                  icon="pi pi-images"
                  className="p-button-outlined p-button-sm"
                  loading={cycleAction === 'reset-onboarding'}
                  disabled={!!cycleAction}
                  onClick={() =>
                    resetCycle(
                      'reset-onboarding',
                      '¿Mostrar de nuevo las pantallas de bienvenida?',
                      'Onboarding reiniciado',
                    )
                  }
                />
                <Button
                  type="button"
                  label="Reiniciar setup"
                  icon="pi pi-map-marker"
                  className="p-button-outlined p-button-sm"
                  loading={cycleAction === 'reset-setup'}
                  disabled={!!cycleAction}
                  onClick={() =>
                    resetCycle(
                      'reset-setup',
                      '¿Pedir de nuevo el cruce habitual?',
                      'Setup reiniciado',
                    )
                  }
                />
                <Button
                  type="button"
                  label="Reiniciar ciclo completo"
                  icon="pi pi-refresh"
                  className="p-button-outlined p-button-danger p-button-sm"
                  loading={cycleAction === 'reset-first-run'}
                  disabled={!!cycleAction}
                  onClick={() =>
                    resetCycle(
                      'reset-first-run',
                      '¿Reiniciar términos, onboarding y setup?',
                      'Ciclo reiniciado',
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="mt-0 mb-3 main-text">
            Reports
            <Badge
              className="ml-2 b-4 relative"
              value={(reports || []).length}
              style={{ bottom: '3px', backgroundColor: '#2020A0' }}
            />
          </h3>
          <DataTable
            value={reports || []}
            paginator
            rows={10}
            emptyMessage="No reports found."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              header="Status"
              body={(row) => (
                <Tag
                  value={row.status}
                  severity={
                    row.status === 'approved'
                      ? 'success'
                      : row.status === 'pending'
                        ? 'warning'
                        : 'danger'
                  }
                />
              )}
            />
            <Column field="crossingName" header="Crossing" />
            <Column field="laneType" header="Lane" />
            <Column field="transportMode" header="Mode" />
            <Column field="pointsAwarded" header="Pts" />
            <Column
              header="Created"
              body={(row) => row.createdAt && new Date(row.createdAt).toLocaleString()}
            />
            <Column
              header="Review"
              body={(row) => (
                <Button
                  icon="pi pi-check-square"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`/community_moderation/${row._id}`)}
                  tooltip="Review report"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
              style={{ width: '6rem', textAlign: 'center' }}
            />
          </DataTable>
        </div>
      </div>
    </Card>
  );
}
